/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import i18n from '@shared/i18n';

/**
 * Returns the default localized column headers used by the skeleton table
 * placeholder (name, type, status, phone, plus a trailing actions column).
 *
 * @returns The list of translated header labels.
 */
const getDefaultColumns = () => [
  i18n.t('table.columns.name', { ns: 'common' }),
  i18n.t('table.columns.type', { ns: 'common' }),
  i18n.t('table.columns.status', { ns: 'common' }),
  i18n.t('table.columns.phone', { ns: 'common' }),
  '',
];

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateFeature {
  icon?: React.ReactNode;
  title: string;
  description: string;
}

export interface EmptyStateStep {
  step: string;
  title: string;
  description: string;
}

export interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  features?: EmptyStateFeature[];
  steps?: EmptyStateStep[];
  variant?: 'default' | 'dashed' | 'skeleton';
  skeletonRows?: number;
  skeletonColumns?: string[];
  children?: React.ReactNode;
}

/**
 * Renders a shimmering table placeholder used as the `skeleton` variant of the
 * empty state while data is loading.
 *
 * @param props Row count and optional column headers for the placeholder grid.
 * @returns The skeleton table element.
 */
function SkeletonTable({ rows = 5, columns = getDefaultColumns() }: { rows?: number; columns?: string[] }) {
  return (
    <div className="relative overflow-hidden rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)]">
      <div
        className="grid gap-4 px-5 py-3.5 border-b border-[var(--border-subtle)] bg-[var(--surface-secondary)]"
        style={{ gridTemplateColumns: `2fr ${columns.slice(1).map(() => '1fr').join(' ')}` }}
      >
        {columns.map((col) => (
          <div key={col} className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">
            {col}
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="grid gap-4 px-5 py-4 border-b border-[var(--border-subtle)]"
          style={{
            gridTemplateColumns: `2fr ${columns.slice(1).map(() => '1fr').join(' ')}`,
            opacity: 0.3 + i * 0.05,
          }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded bg-[var(--surface-tertiary)]" />
            <div className="h-3 rounded bg-[var(--surface-tertiary)]" style={{ width: `${60 + i * 10}px` }} />
          </div>
          {columns.slice(1, -1).map((_, ci) => (
            <div key={ci} className="h-3 rounded bg-[var(--surface-tertiary)] self-center" style={{ width: `${50 + ci * 10}px` }} />
          ))}
          <div className="w-5 h-3 rounded bg-[var(--surface-tertiary)] self-center" />
        </div>
      ))}
    </div>
  );
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  features,
  steps,
  variant = 'default',
  skeletonRows = 5,
  skeletonColumns,
  children,
}) => {
  const borderClass = variant === 'dashed'
    ? 'border-2 border-dashed border-[var(--border-subtle)]'
    : 'border border-[var(--border-subtle)]';

  if (variant === 'skeleton') {
    return (
      <div className="relative">
        <SkeletonTable rows={skeletonRows} columns={skeletonColumns} />
        <div className="absolute inset-0 flex flex-col items-center justify-center pt-16 bg-gradient-to-b from-transparent via-[var(--surface-card)]/85 to-[var(--surface-card)]">
          <div className="w-16 h-16 rounded-full bg-[var(--surface-secondary)] border-2 border-[var(--border-subtle)] flex items-center justify-center mb-4">
            {icon}
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1.5">
            {title}
          </h3>
          <p className="text-[13px] text-[var(--text-secondary)] mb-5 max-w-[320px] text-center leading-relaxed">
            {description}
          </p>
          {action && (
            <button
              onClick={action.onClick}
              className="flex items-center gap-1.5 bg-[var(--seed-base)] text-white border-none rounded-(--radius-md) px-5 py-2.5 text-[13px] font-semibold cursor-pointer hover:opacity-90 transition-opacity"
            >
              {action.label}
            </button>
          )}
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-(--radius-xl) ${borderClass} bg-[var(--surface-card)] px-6 py-6 flex flex-col items-center text-center flex-1 justify-center min-h-0 overflow-hidden`}>
      <div className="w-16 h-16 rounded-full bg-[var(--surface-secondary)] flex items-center justify-center mb-4">
        {icon}
      </div>

      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1.5">
        {title}
      </h3>
      <p className="text-sm text-[var(--text-secondary)] mb-5 max-w-[440px] leading-relaxed">
        {description}
      </p>

      <div className="flex gap-3 flex-wrap justify-center">
        {action && (
          <button
            onClick={action.onClick}
            className="flex items-center gap-2 bg-[var(--seed-base)] text-white border-none rounded-(--radius-md) px-5 py-2.5 text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity"
          >
            {action.label}
          </button>
        )}
        {secondaryAction && (
          <button
            onClick={secondaryAction.onClick}
            className="flex items-center gap-2 bg-transparent text-[var(--seed-base)] border border-[var(--seed-base)] rounded-(--radius-md) px-5 py-2.5 text-sm font-semibold cursor-pointer hover:bg-[var(--seed-base)]/5 transition-colors"
          >
            {secondaryAction.label}
          </button>
        )}
      </div>

      {steps && steps.length > 0 && (
        <div className="mt-6 flex gap-6 w-full max-w-[520px] justify-center">
          {steps.map((s) => (
            <div key={s.step} className="flex flex-col items-center flex-1">
              <div className="w-7 h-7 rounded-full bg-[var(--seed-base)] text-white flex items-center justify-center text-xs font-bold mb-1.5">
                {s.step}
              </div>
              <div className="text-xs font-semibold text-[var(--text-primary)] mb-0.5">{s.title}</div>
              <div className="text-xs text-[var(--text-tertiary)] leading-snug">{s.description}</div>
            </div>
          ))}
        </div>
      )}

      {features && features.length > 0 && (
        <div className="mt-6 grid gap-3 w-full max-w-[700px] grid-cols-2 sm:grid-cols-4">
          {features.map((f) => (
            <div key={f.title} className="p-3 bg-[var(--surface-secondary)] rounded-(--radius-lg) text-left flex gap-2.5 items-start min-w-0">
              {f.icon && (
                <div className="w-6 h-6 rounded bg-[var(--surface-tertiary)] flex items-center justify-center shrink-0 mt-0.5">
                  {f.icon}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-[var(--text-primary)] break-words">{f.title}</div>
                <div className="text-xs text-[var(--text-secondary)] leading-snug mt-0.5 break-words">{f.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {children}
    </div>
  );
};

export default EmptyState;
